import { useEffect, useMemo, useRef, useState } from "react";
import MainLayout from "./MainLayout";
import Swal from "sweetalert2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";

import {
  Person,
  People,
  Business,
  Assignment,
  Inventory,
  PhotoLibrary,
  HideImage,
  ContactMail,
  Timeline,
  TrendingUp,
  TrendingDown,
  Download,
  CalendarMonth,
  Info,
  PeopleAlt,
  BlockOutlined,
} from "@mui/icons-material";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import DrawIcon from "@mui/icons-material/Draw";
import { IconButton } from "@mui/material";
import axios from "axios";
import Apiurl from "../../Helpers/apiUrl";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchAdminDashCount,
  fetchAdminDashTableData,
} from "../../redux/slices/dashSlice";
import MultiSelectWithCheckbox from "../utils/MultiSelectWithCheckbox";
import LoadingIndicator from "../utils/LoadingIndicator";
import DateRangeFilter from "../utils/DateRangeFilter";
import RoleWiseQuotation from "../utils/RoleWiseQuotation";
import RoleIndicator from "../utils/RoleIndicator";
import { useDrawer } from "../../DrawerContext";
import AdminDashStats from "../utils/AdminDashStats";
import AdminDashDonut from "../utils/AdminDashDonut";
import DashboardChart from "../utils/DashboardChart";
import DashboardMiniTable from "./DashboardMiniTable";
import { fetchRegion, fetchState } from "../../redux/slices/clientSlice";
import { fetchUserOpt } from "../../redux/slices/optionSlice";
import UserService from "../../Services/UserList";
import FilterBar2 from "./admin_dashboard/FilterBar2";
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend
);

const roleColors = {
  // 3 Primary Business Anchors (Solid, distinct blocks)
  IC: "#0f3b63",              // Deep Corporate Navy
  BD: "#0d9488",              // Sharp Emerald Teal
  DSP: "#f59e0b",             // Premium Amber

  // 2 Light Structural Tints (Instantly recognizable contrast)
  BSP: "#38bdf8",             // Clean Sky Blue
  'Sales Enabler': "#99f6e4", // Light Mint Teal

  // 2 Definitive Matte Neutrals (Grounds the chart cleanly)
  Dealer: "#1e293b",          // Dark Matte Slate
  Studio: "#64748b"           // Balanced Steel Gray
};

const FY_MONTH_ORDER = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const AdminDashboard = () => {
  const { drawerVisible } = useDrawer();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentYears = new Date().getFullYear();
  const [loading, setLoading] = useState(false);

  const [year, setYear] = useState(`${new Date().getFullYear()}`);
  const [latestClientData, setLatestClientData] = useState([]);
  const [latestUserData, setLatestUserData] = useState([]);
  const [latestSkuData, setLatestSkuData] = useState([]);
  const [brand, setBrand] = useState([]);
  const [topCollection, setTopCollection] = useState([]);
  const [toDyaLogin, settoDyaLogin] = useState([]);
  const [g3Product, setG3Product] = useState([]);
  const [g4Product, setG4Product] = useState([]);
  const [g5Product, setG5Product] = useState([]);
  const { userOpt } = useSelector((s) => s.options);
  const [roleId, setRoleId] = useState([]);
  const [selectedYear, setSelectedYear] = useState([currentYears]);
  const { adminTableData, getStatus } = useSelector(
    (state) => state.dash
  );

  const [selectedRegion, setSelectedRegion] = useState([]);
  const [selectedState, setSelectedState] = useState([]);
  const [channelOpt, setChannelOpt] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState([]);

  const [headerHeight, setHeaderHeight] = useState('0rem');
  const headerRef = useRef(null);

  useEffect(() => {
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(`${headerRef.current.offsetHeight}px`);
      }
    };

    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);

    return () => window.removeEventListener('resize', updateHeaderHeight);
  }, [drawerVisible]);

  const getSessionStorage = (key, defaultValue) => {
    const stored = sessionStorage.getItem(key);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        console.error(`Error parsing sessionStorage key: ${key}`, e);
        return defaultValue;
      }
    }
    return defaultValue;
  };

  const setSessionStorage = (key, value) => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      console.error(`Error setting sessionStorage key: ${key}`, e);
    }
  };

  const [selectedUserId, _setSelectedUserId] = useState(
    getSessionStorage("users", [])
  );
  const [fromDate, _setFromDate] = useState(
    getSessionStorage("dateFrom", null)
  );
  const [toDate, _setToDate] = useState(getSessionStorage("dateTo", null));
  const [selectedMonths, _setSelectedMonths] = useState(() => {
    const stored = sessionStorage.getItem("months");
    if (stored) {
      try { return JSON.parse(stored); } catch (e) { }
    }
    const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
    const currentCalendarMonth = new Date().getMonth() + 1;
    const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
    return fullFYMonths.slice(0, currentMonthIndex + 1);
  });

  const setSelectedUserId = (newValue) => {
    _setSelectedUserId(newValue);
    setSessionStorage("users", newValue);
  };

  const setFromDate = (newValue) => {
    _setFromDate(newValue);
    setSessionStorage("dateFrom", newValue);
  };

  const setToDate = (newValue) => {
    _setToDate(newValue);
    setSessionStorage("dateTo", newValue);
  };

  const setSelectedMonths = (newValue) => {
    _setFromDate(null);
    _setToDate(null);
    _setSelectedMonths(newValue);
    setSessionStorage("months", newValue);
  };

  const toggleMonth = (m) => {
    const updated = selectedMonths.includes(m)
      ? selectedMonths.filter((x) => x !== m)
      : [...selectedMonths, m];
    setSelectedMonths(updated);
  };

  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false);

  const monthRangeLabel = useMemo(() => {
    if (selectedMonths.length === 12) return "Full FM";
    if (selectedMonths.length === 0) return "Select Months";
    if (selectedMonths.length === 1) return MONTHS[selectedMonths[0] - 1]?.slice(0, 3);
    const sorted = [...selectedMonths].sort(
      (a, b) => FY_MONTH_ORDER.indexOf(a) - FY_MONTH_ORDER.indexOf(b)
    );
    return `${MONTHS[sorted[0] - 1]?.slice(0, 3)} (+${selectedMonths.length - 1})`;
  }, [selectedMonths]);

  useEffect(() => {
    const isUsingDateRange = fromDate || toDate;
    if (!isUsingDateRange && (selectedYear.length === 0 || selectedMonths.length === 0)) {
      return;
    }

    const payload = {
      user_id:
        selectedUserId.length > 0
          ? selectedUserId?.map((u) => u.value)
          : null,
      month: fromDate || toDate ? null : selectedMonths.join(","),
      year: fromDate || toDate ? null : selectedYear.join(","),
      from: fromDate,
      to: toDate,
      role: roleId.length > 0 ? roleId : null,
      region: selectedRegion.length > 0 ? selectedRegion : null,
      state: selectedState.length > 0 ? selectedState : null,
      channel: selectedChannels.length > 0 ? selectedChannels.join(",") : null,
    };

    // dispatch(fetchAdminDashCount(payload));
    dispatch(fetchAdminDashTableData(payload));
  }, [
    selectedUserId,
    selectedMonths,
    selectedYear,
    fromDate,
    toDate,
    roleId,
    selectedRegion,
    selectedState,
    selectedChannels,
  ]);

  const userOptions = userOpt
    ? userOpt?.map((emp) => ({
      value: emp.id,
      label: `${emp.name} ${emp.last_name}`,
    }))
    : [];

  useEffect(() => {
    const isUsingDateRange = fromDate || toDate;
    const hasData = isUsingDateRange || (selectedYear.length > 0 && selectedMonths.length > 0);

    setLatestClientData(hasData ? (adminTableData?.clientQuation || []) : []);
    setG3Product(hasData ? (adminTableData?.g3Product || []) : []);
    setG4Product(hasData ? (adminTableData?.g4Product || []) : []);
    setG5Product(hasData ? (adminTableData?.g5Product || []) : []);
    setLatestSkuData(hasData ? (adminTableData?.movingproduct || []) : []);
    settoDyaLogin(hasData ? (adminTableData?.todayLogin || []) : []);
    setBrand(hasData ? (adminTableData?.topBrandProduct || []) : []);
    setTopCollection(hasData ? (adminTableData?.topCollectionProduct || []) : []);
    setLatestUserData(hasData ? (adminTableData?.userQuotation || []) : []);
  }, [adminTableData, selectedYear, selectedMonths, fromDate, toDate]);

  const downloadFile = async (excelType = "sku") => {
    try {
      setLoading(true);
      const user = localStorage.getItem("u_token");
      const response = await axios.get(
        `${Apiurl}admin/dashboardExcel?type=${excelType}&month=${fromDate || toDate ? null : selectedMonths.join(",")
        }&year=${fromDate || toDate ? null : selectedYear.join(",")}&user_id=${selectedUserId.length > 0 ? selectedUserId?.map((u) => u.value) : null
        }&from=${fromDate}&to=${toDate}&role=${roleId.length > 0 ? roleId.join(",") : ""}`,
        {
          headers: {
            Authorization: "Bearer " + user,
            Accept:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
          responseType: "blob",
        }
      );

      const contentDisposition = response.headers["content-disposition"];
      let filename = "export.xlsx";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match && match[1]) {
          filename = match[1];
        }
      }

      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      Swal.fire({
        icon: "warning",
        title: "Download Failed",
        text: error.message || "Something went wrong while downloading.",
        timer: 3000,
      });
    }
  };

  useEffect(() => {
    dispatch(fetchRegion());
    dispatch(fetchUserOpt());
    UserService.GetChannels().then(response => {
      if (response && response.data) {
        setChannelOpt(response.data.map(ch => ({
          value: ch.id,
          label: ch.channel_name
        })));
      }
    }).catch(console.error);
  }, []);

  const currentYear = new Date().getFullYear();
  const startYear = 2023;
  const years = Array.from(
    { length: currentYear - startYear + 1 },
    (_, i) => currentYear - i
  );
  const fyLabel = (y) => `${y}-${y + 1}`;

  const handlePreview = (title) => {
    navigate("/admin/preview", {
      state: {
        title,
        userOptions,
        role: roleId,
        channel:selectedChannels,
        user: selectedUserId,
        region: selectedRegion,
        state: selectedState,
        month: selectedMonths,
        year: selectedYear,
        from: fromDate,
        to: toDate,
      },
    });
  };

  const handleClearFilters = () => {
    setSelectedUserId([]);
    setRoleId([]);
    setSelectedChannels([]);
    setFromDate(null);
    setToDate(null);
    const fullFYMonths = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
    const currentCalendarMonth = new Date().getMonth() + 1;
    const currentMonthIndex = fullFYMonths.indexOf(currentCalendarMonth);
    setSelectedMonths(fullFYMonths.slice(0, currentMonthIndex + 1));
    setSelectedYear([currentYears]);
    setSelectedRegion([]);
    setSelectedState([]);
  };

  const loginTableConfig = {
    title: "Today's Login Users",
    data: toDyaLogin,
    loading: !toDyaLogin,
    onPreview: () => handlePreview("Today Login Users"),
    columns: [
      {
        label: "SL NO.",
        width: "50px",
        render: (row, index) => <span className="text-slate-400 font-mono">{index + 1}</span>
      },
      {
        label: "Name",
        render: (row) => <span className="font-bold text-slate-900 capitalize">{row.name} {row.last_name || ""}</span>
      },
      {
        label: "Last Login",
        render: (row) => (
          <span className="text-indigo-600 font-bold bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100">
            {new Date(row.last_login_time).toLocaleString("en-GB")}
          </span>
        )
      }
    ]
  };


  const topCollectionConfig = {
    title: "Top Collection Usage",
    data: topCollection, // Your API state
    loading: !topCollection,
    onPreview: () => handlePreview("Top Collection Usage"),
    onDownload: () => downloadFile("collection"), // Added for this specific table
    accentColor: "#f59e0b", // Yellow/Amber accent to match your style
    columns: [
      {
        label: "SL NO.",
        width: "50px",
        render: (item, index) => <span className="text-slate-600 font-mono">{index + 1}</span>
      },
      {
        label: "Collection",
        key: "collection" // Simple key usage
      },
      {
        label: (
          <div className="flex items-center gap-1 cursor-progress" title="Number of quotations made">
            <Info sx={{ fontSize: 14 }} /> Quote
          </div>
        ),
        render: (item) => (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[12px] font-black bg-amber-100 text-amber-700 border border-amber-200">
            {item.quotation_count}
          </span>
        )
      },
      {
        label: (
          <div className="flex items-center gap-1 cursor-progress" title="Number of times collection used">
            <Info sx={{ fontSize: 14 }} /> Usage
          </div>
        ),
        render: (item) => (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[12px] font-black bg-amber-50 text-amber-600 border border-amber-100">
            {item.usage_count}
          </span>
        )
      }
    ]
  };

  const topBrandConfig = {
    title: "Top Brand Usage",
    data: brand,
    loading: !brand,
    onPreview: () => handlePreview("Brand Usage"),
    onDownload: () => downloadFile("brand"),
    accentColor: "#10b981",
    columns: [
      {
        label: "SL NO.",
        width: "50px",
        render: (item, index) => <span className="text-slate-600 font-mono text-[12px] ">{(index + 1)}</span>
      },
      {
        label: "Brand",
        key: "brand",
        render: (item) => <span className="font-bold text-slate-800 tracking-tight">{item.brand}</span>
      },
      {
        label: (
          <div className="flex items-center gap-1 w-20 cursor-progress" title="Number of users used the brand in quotation">
            <Info sx={{ fontSize: 14 }} /> Used By
          </div>
        ),
        render: (item) => (
          <span className="inline-flex items-center px-3 py-0.5 rounded-full text-[12px] font-black bg-emerald-50 text-emerald-700 border border-emerald-100">
            <Person sx={{ fontSize: 15 }} /> {item.user_count || 0}
          </span>
        )
      },
      {
        label: (
          <div className="flex items-center gap-1 cursor-progress" title="Number of quotations made">
            <Info sx={{ fontSize: 14 }} /> Quote
          </div>
        ),
        render: (item) => (
          <span className="inline-flex items-center px-3 py-0.5 rounded-full text-[12px] font-black bg-emerald-50 text-emerald-700 border border-emerald-100">
            {item.quotation_count}
          </span>
        )
      },
      {
        label: (
          <div className="flex items-center gap-1 cursor-progress" title="Number of times brand used in quotation">
            <Info sx={{ fontSize: 14 }} /> Usage
          </div>
        ),
        render: (item) => (
          <span className="inline-flex items-center px-3 py-0.5 rounded-full text-[12px] font-black bg-emerald-50 text-emerald-700 border border-emerald-100">
            {item.usage_count}
          </span>
        )
      }
    ]
  };

  const g5SegmentConfig = {
    title: "G5 Price Segment Product",
    data: g5Product,
    loading: !g5Product,
    onPreview: () => handlePreview("G5Segment"),
    onDownload: () => downloadFile("G5Segment"),
    accentColor: "#334155",
    maxHeight: "450px",
    columns: [
      {
        label: "SL NO.",
        width: "40px",
        render: (item, index) => <span className="text-slate-600 font-mono text-[12px]">{(index + 1)}</span>
      },
      {
        label: "Sku Code",
        render: (item) => <span className="font-black text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-100 text-[12px] uppercase tracking-tighter">{item.sku_code}</span>
      },
      {
        label: (
          <div className="flex items-center gap-1 cursor-progress" title="Quotations made">
            <Info sx={{ fontSize: 13 }} /> Quote
          </div>
        ),
        render: (item) => (
          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[12px] font-black bg-slate-100 text-slate-700 border border-slate-200">
            {item.quotation_count}
          </span>
        )
      },
      {
        label: (
          <div className="flex items-center gap-1 cursor-progress" title="SKU Usage">
            <Info sx={{ fontSize: 13 }} /> Usage
          </div>
        ),
        render: (item) => (
          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[12px] font-black bg-emerald-50 text-emerald-700 border border-emerald-100">
            {item.usage_count}
          </span>
        )
      },
      {
        label: "Description",
        render: (item) => <span className="truncate max-w-[150px] block" title={item.product_desc}>{item.product_desc}</span>
      },
      {
        label: "Brand",
        key: "brand"
      },
      {
        label: "Collection",
        key: "collection"
      },
      {
        label: "Finish",
        render: (item) => <span className="text-[12px] text-slate-500 italic">{item.finish}</span>
      }
    ]
  };

  const g4SegmentConfig = {
    title: "G4 Price Segment Product",
    data: g4Product,
    loading: !g4Product,
    onPreview: () => handlePreview("G4Segment"),
    onDownload: () => downloadFile("G4Segment"),
    accentColor: "#f59e0b",
    maxHeight: "450px",
    columns: [
      {
        label: "SL NO.",
        width: "40px",
        render: (item, index) => <span className="text-slate-600 font-mono text-[12px]">{index + 1}</span>
      },
      {
        label: "Sku Code",
        render: (item) => (
          <span className="font-black text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-100 text-[12px] uppercase tracking-tighter">
            {item.sku_code}
          </span>
        )
      },
      {
        label: (
          <div className="flex items-center gap-1 cursor-progress" title="Number of quotations made">
            <Info sx={{ fontSize: 13 }} /> Quote
          </div>
        ),
        render: (item) => (
          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[12px] font-black bg-amber-100 text-amber-800 border border-amber-200">
            {item.quotation_count}
          </span>
        )
      },
      {
        label: (
          <div className="flex items-center gap-1 cursor-progress" title="Number of times SKU used in quotation">
            <Info sx={{ fontSize: 13 }} /> Usage
          </div>
        ),
        render: (item) => (
          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[12px] font-black bg-amber-50 text-amber-700 border border-amber-100">
            {item.usage_count}
          </span>
        )
      },
      {
        label: "Description",
        render: (item) => (
          <span className="truncate max-w-[150px] block font-medium text-slate-700" title={item.product_desc}>
            {item.product_desc}
          </span>
        )
      },
      {
        label: "Brand",
        key: "brand"
      },
      {
        label: "Collection",
        key: "collection"
      },
      {
        label: "Finish",
        render: (item) => (
          <span className="text-[12px] font-bold text-slate-500 uppercase tracking-tight">
            {item.finish}
          </span>
        )
      }
    ]
  };

  const g3SegmentConfig = {
    title: "G3 Price Segment Product",
    data: g3Product,
    loading: !g3Product,
    onPreview: () => handlePreview("G3Segment"),
    onDownload: () => downloadFile("G3Segment"),
    accentColor: "#64748b",
    maxHeight: "450px",
    columns: [
      {
        label: "SL NO.",
        width: "40px",
        render: (item, index) => <span className="text-slate-600 font-mono text-[12px]">{index + 1}</span>
      },
      {
        label: "Sku Code",
        render: (item) => (
          <span className="font-black text-slate-700 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-200 text-[12px] uppercase tracking-tighter">
            {item.sku_code}
          </span>
        )
      },
      {
        label: (
          <div className="flex items-center gap-1 cursor-progress" title="Number of quotations made">
            <Info sx={{ fontSize: 13 }} /> Quote
          </div>
        ),
        render: (item) => (
          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[12px] font-black bg-slate-100 text-slate-800 border border-slate-200">
            {item.quotation_count}
          </span>
        )
      },
      {
        label: (
          <div className="flex items-center gap-1 cursor-progress" title="Number of times SKU used in quotations">
            <Info sx={{ fontSize: 13 }} /> Usage
          </div>
        ),
        render: (item) => (
          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[12px] font-black bg-slate-50 text-slate-600 border border-slate-100">
            {item.usage_count}
          </span>
        )
      },
      {
        label: "Description",
        render: (item) => (
          <span className="truncate max-w-[150px] block font-medium text-slate-700" title={item.product_desc}>
            {item.product_desc}
          </span>
        )
      },
      {
        label: "Brand",
        key: "brand"
      },
      {
        label: "Collection",
        key: "collection"
      },
      {
        label: "Finish",
        render: (item) => (
          <span className="text-[12px] font-bold text-slate-400 uppercase">
            {item.finish}
          </span>
        )
      }
    ]
  };

  const fastMovingConfig = {
    title: "Fast Moving Products",
    data: latestSkuData,
    loading: !latestSkuData,
    onPreview: () => handlePreview("Fast Moving Products"),
    onDownload: () => downloadFile("FastMoving"),
    accentColor: "#7c3aed",
    maxHeight: "450px",
    columns: [
      {
        label: "SL NO.",
        width: "40px",
        render: (item, index) => <span className="text-slate-600 font-mono text-[12px]">{index + 1}</span>
      },
      {
        label: "SKU Code",
        render: (item) => (
          <span className="font-black text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded border border-purple-100 text-[12px] uppercase tracking-tighter">
            {item.sku_code}
          </span>
        )
      },
      {
        label: (
          <div className="flex items-center gap-1 cursor-progress" title="Number of quotations made">
            <Info sx={{ fontSize: 13 }} /> Quote
          </div>
        ),
        render: (item) => (
          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[12px] font-black bg-purple-100 text-purple-800 border border-purple-200">
            {item.quotation_count}
          </span>
        )
      },
      {
        label: (
          <div className="flex items-center gap-1 cursor-progress" title="Number of times used in quotations">
            <Info sx={{ fontSize: 13 }} /> Usage
          </div>
        ),
        render: (item) => (
          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[12px] font-black bg-purple-50 text-purple-600 border border-purple-100">
            {item.usage_count}
          </span>
        )
      },
      {
        label: "Description",
        render: (item) => (
          <span className="truncate max-w-[150px] block font-medium text-slate-700" title={item.product_desc}>
            {item.product_desc}
          </span>
        )
      },
      {
        label: "Brand",
        key: "brand"
      },
      {
        label: "Collection",
        key: "collection"
      },
      {
        label: "Finish",
        render: (item) => (
          <span className="text-[12px] font-bold text-slate-400 uppercase tracking-tight">
            {item.finish}
          </span>
        )
      }
    ]
  };


  const topUsersConfig = {
    title: "Top Users by Quotations",
    data: latestUserData,
    loading: !latestUserData,
    onPreview: () => handlePreview("Top Users by Quotations"),
    onDownload: () => downloadFile("TopUsers"),
    accentColor: "#0d9488",
    columns: [
      {
        label: "SL NO.",
        width: "50px",
        render: (item, index) => <span className="text-slate-600 font-mono text-[12px]">{index + 1}</span>
      },
      {
        label: "Username",
        render: (item) => (
          <span className="font-bold text-slate-800 tracking-tight">
            {item.username}
          </span>
        )
      },
      {
        label: (
          <div className="flex items-center gap-1 cursor-progress" title="Quotations made by user">
            <Info sx={{ fontSize: 13 }} /> Quotations
          </div>
        ),
        render: (item) => (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-black bg-emerald-50 text-emerald-700 border border-emerald-100">
            {item.project_count}
          </span>
        )
      }
    ]
  };

  const topProjectsConfig = {
    title: "Top Projects by Quotations",
    data: latestClientData,
    loading: !latestClientData,
    onPreview: () => handlePreview("Top Projects by Quotations"),
    onDownload: () => downloadFile("TopClients"),
    accentColor: "#be123c",
    columns: [
      {
        label: "SL NO.",
        width: "50px",
        render: (item, index) => <span className="text-slate-600 font-mono text-[12px]">{index + 1}</span>
      },
      {
        label: "Client Name",
        render: (item) => (
          <span className="font-bold text-slate-800 tracking-tight capitalize">
            {item.clientname}
          </span>
        )
      },
      {
        label: (
          <div className="flex items-center gap-1 cursor-progress " title="Quotations made for client">
            <Info sx={{ fontSize: 13 }} /> Quotations
          </div>
        ),
        render: (item) => (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-black bg-red-50 text-red-700 border border-red-100">
            {item.project_count}
          </span>
        )
      }
    ]
  };

  const performanceConfigs = [topUsersConfig, topProjectsConfig, loginTableConfig];
  const productConfigs = [fastMovingConfig, g5SegmentConfig, g4SegmentConfig, g3SegmentConfig];
  const marketConfigs = [topBrandConfig, topCollectionConfig];


  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);

  const toggleYear = (y) => {
    setFromDate(null);
    setToDate(null);
    setSelectedYear((prev) =>
      prev.includes(y)
        ? prev.length > 1
          ? prev.filter((item) => item !== y)
          : prev
        : [...prev, y]
    );
  };

  const yearRangeLabel = useMemo(() => {
    if (selectedYear.length === 0) return "Select Year";
    if (selectedYear.length === 1) return fyLabel(selectedYear[0]);
    return `${fyLabel(selectedYear[0])} (+${selectedYear.length - 1})`;
  }, [selectedYear]);

  const roleOptions = [
    { label: "IC", value: 2 },
    { label: "BSP", value: 3 },
    { label: "DSP", value: 4 },
    { label: "BD", value: 5 },
    { label: "Sales Enabler", value: 6 },
    { label: "Dealer", value: 7 },
    { label: "Studio", value: 8 },
  ];

  const { regionOpt, stateOpt } = useSelector((state) => state.adminOption);

  const yearOptions = years.map((y) => ({ label: fyLabel(y), value: String(y) }));
  const formattedUserOptions = (userOpt || []).map((emp) => ({
    label: `${emp.name} ${emp.last_name || ""}`.trim(),
    value: emp.id,
  }));

  // Filter Configurations Array
  const filterConfigs = [
    {
      id: "role",
      label: "Role",
      options: roleOptions,
      selected: roleOptions.filter((s) => roleId.includes(s.value)),
    },
    {
      id: "user",
      label: "Users",
      options: formattedUserOptions,
      selected: formattedUserOptions.filter((u) =>
        selectedUserId.some((selected) => String(selected.value ?? selected) === String(u.value))
      ),
    },
    {
      id: "channel",
      label: "Channel",
      options: channelOpt,
      selected: channelOpt.filter((c) => selectedChannels.includes(c.value)),
    },
    {
      id: "region",
      label: "Region",
      options: regionOpt,
      selected: regionOpt.filter((s) => selectedRegion.includes(s.value)),
    },
    {
      id: "state",
      label: "State",
      options: stateOpt,
      selected: stateOpt.filter((s) => selectedState.includes(s.value)),
    },
    {
      id: "year",
      label: "Year (FY)",
      options: yearOptions,
      selected: yearOptions.filter((y) => selectedYear.map(String).includes(y.value)),
    },
  ];
  const handleFilterChange = (filterId, nextSelected) => {
    switch (filterId) {
      case "role": {
        const roles = nextSelected.map((r) => (typeof r === "object" ? r.value ?? r.id : r));
        setRoleId(roles);
        break;
      }
      case "user": {
        const updatedUsers = nextSelected.map((u) =>
          typeof u === "object" ? { value: u.value ?? u.id, label: u.label ?? u.name } : { value: u, label: String(u) }
        );
        setSelectedUserId(updatedUsers);
        break;
      }
      case "channel": {
        const channels = nextSelected.map((c) => (typeof c === "object" ? c.value ?? c.id : c));
        setSelectedChannels(channels);
        break;
      }
      case "region": {
        const regions = nextSelected.map((r) => (typeof r === "object" ? r.value ?? r.id : r));
        setSelectedRegion(regions);
        setSelectedState([]); // Reset state selection when region changes
        break;
      }
      case "state": {
        const states = nextSelected.map((s) => (typeof s === "object" ? s.value ?? s.id : s));
        setSelectedState(states);
        break;
      }
      case "year": {
        const selectedYears = nextSelected.map((y) => Number(typeof y === "object" ? y.value : y));
        setSelectedYear(selectedYears);
        if (selectedYears.length === 0) {
          setSelectedMonths([]);
        }
        break;
      }
      default:
        break;
    }
  };

  return (
    <MainLayout title={"Dashboard"} topbar={false} disableMenu={true}>
      <LoadingIndicator show={getStatus === "pending" || loading} />
      <div className="bg-white text-gray-900 min-h-screen  px-6">
        {/* Header*/}
        <div
          ref={headerRef}

          className="fixed z-40  "
          style={{
            left: drawerVisible ? "240px" : "80px",
            right: 0,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <FilterBar2
            filters={filterConfigs}
            onFilterChange={handleFilterChange}
            selectedMonth={selectedMonths}
            setSelectedMonth={setSelectedMonths}
            selectedFromDate={fromDate}
            setSelectedFromDate={setFromDate}
            selectedToDate={toDate}
            setSelectedToDate={setToDate}
            onClearFilters={handleClearFilters}
          />
        </div>

        <div
          style={{
            height: headerHeight,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
          }}
        />

        {/* Main content */}
        <div className="-mx-3  space-y-4   -pt-12 pb-12">

          <RoleWiseQuotation
            payload={{
              user_id:
                selectedUserId.length > 0
                  ? selectedUserId?.map((u) => u.value)
                  : null,
              month: fromDate || toDate ? null : selectedMonths.join(","),
              year: fromDate || toDate ? null : selectedYear.join(","),
              from: fromDate,
              to: toDate,
              role: roleId.length > 0 ? roleId : null,
              region: selectedRegion.length > 0 ? selectedRegion : null,
              state: selectedState,
            }}
          />

          <DashboardMiniTable
            groupTitle="Product Intelligence"
            configs={productConfigs}
          />

          <DashboardMiniTable
            groupTitle="Team Performance"
            configs={performanceConfigs}
          />


          <DashboardMiniTable
            groupTitle="Market Trends"
            configs={marketConfigs}
          />

        </div>
      </div>
    </MainLayout>
  );
};

export default AdminDashboard;